import { useCallback, useState } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';
import { AnalyticsOverview } from './AnalyticsOverview';
import { AnalyticsChart } from './AnalyticsChart';
import './Analytics.css';


export function Analytics() {
  const {
    data,
    periodStats,
    backfillStatus,
    loading,
    error,
    filters,
    updateFilters,
    triggerBackfill
  } = useAnalytics();

  const [selectedPeriod, setSelectedPeriod] = useState<'weekly' | 'monthly' | 'quarterly' | 'six_month' | 'annual'>('monthly');

  const handlePeriodChange = useCallback((period: 'weekly' | 'monthly' | 'quarterly' | 'six_month' | 'annual') => {
    setSelectedPeriod(period);
    updateFilters({ period });
  }, [updateFilters]);

  const getRatingColor = (rating: string): string => {
    switch (rating) {
      case 'excellent': return '#22c55e';
      case 'good': return '#84cc16';
      case 'moderate': return '#eab308';
      case 'poor': return '#f97316';
      case 'very_poor': return '#ef4444';
      default: return '#64748b';
    }
  };

  return (
    <div className="analytics">
      <div className="analytics-header">
        <h2>Solar Performance Analytics</h2>
        <p className="analytics-subtitle">
          Historical performance analysis with conservative estimates
        </p>
      </div>

      <div className="analytics-body">
        {/* Backfill Status */}
        {backfillStatus && backfillStatus.is_running && (
          <div className="backfill-progress">
            <div className="progress-info">
              <span>Generating historical data...</span>
              <span>{backfillStatus.progress} / {backfillStatus.total_days} days</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${(backfillStatus.progress / backfillStatus.total_days) * 100}%` }}
              />
            </div>
          </div>
        )}

        {loading && !backfillStatus?.is_running && (
          <div className="loading-state">Loading analytics...</div>
        )}

        {error && (
          <div className="error-state">
            <p>{error}</p>
            <button className="rebuild-btn" onClick={() => triggerBackfill(365, true)}>
              Rebuild History (365 days)
            </button>
          </div>
        )}

        {data && !loading && (
          <div className="analytics-content">
            {/* Performance Rating Badge */}
            <div className="performance-rating">
              <div
                className="rating-badge"
                style={{ backgroundColor: getRatingColor(data.insights.performance_rating) }}
              >
                {data.insights.performance_rating.replace('_', ' ').toUpperCase()}
              </div>
              <p className="rating-summary">
                <strong>SOLAR FAILURE:</strong> {data.insights.summary}
              </p>
            </div>

            {/* Overview Cards */}
            <AnalyticsOverview
              periodStats={periodStats || null}
              thresholdStats={data.custom_threshold}
              selectedPeriod={selectedPeriod}
              onPeriodChange={handlePeriodChange}
            />

            {/* Performance Chart */}
            <div className="chart-section">
              <h3>Daily Performance Trends</h3>
              <p className="chart-subtitle">
                Performance ratio over time (threshold: {(filters.threshold * 100).toFixed(0)}%)
              </p>
              <AnalyticsChart
                data={data.daily_data}
                height={320}
              />
            </div>

            {/* Key Findings */}
            {data.insights.key_findings.length > 0 && (
              <div className="findings-section">
                <h3>Damning Performance Failures</h3>
                <div className="findings-grid">
                  {data.insights.key_findings.map((finding, i) => (
                    <div key={i} className="finding-card">
                      <div className="finding-icon">💀</div>
                      <div className="finding-text">{finding}</div>
                    </div>
                  ))}
                  {/* Add more critical insights */}
                  <div className="finding-card">
                    <div className="finding-icon">⚠️</div>
                    <div className="finding-text">
                      Monthly output at {(data.monthly.avg_ratio * 100).toFixed(1)}% of conservative target –
                      {(100 - data.monthly.avg_ratio * 100).toFixed(1)}% deficit = {(data.monthly.deficit_mwh).toLocaleString()} MWh lost to inefficiency.
                    </div>
                  </div>
                  <div className="finding-card">
                    <div className="finding-icon">💸</div>
                    <div className="finding-text">
                      Capacity factor {(data.monthly.avg_capacity_factor * 100).toFixed(1)}% vs UK average 10.8% –
                      this money-pit generates ${(data.monthly.avg_actual_mwh * 0.15).toFixed(0)}p/kWh vs grid price 28p/kWh.
                    </div>
                  </div>
                  <div className="finding-card">
                    <div className="finding-icon">🌧️</div>
                    <div className="finding-text">
                      Over {data.below_50_percent.below_threshold} days below 50% threshold ({data.below_50_percent.percentage}%) –
                      clouds turn this solar farm into an expensive lawn ornament.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recommendation */}
            {data.insights.recommendation && (
              <div className="recommendation-section">
                <h3>Harsh Reality Check</h3>
                <div className="recommendation-card critical">
                  <div className="recommendation-icon">🚫</div>
                  <div className="recommendation-text">
                    <strong>DON'T BUILD THIS:</strong> {data.insights.recommendation}
                    This solar farm would be a catastrophic financial sinkhole disguised as green energy.
                  </div>
                </div>
              </div>
            )}

            {/* Data Footer */}
            <div className="data-footer">
              <span className="data-range">
                Data: {data.data_range.start} to {data.data_range.end}
              </span>
              <span className="data-count">
                {data.record_count} records analyzed
              </span>
              <span className="data-source">
                Conservative estimates using PVGIS UK data
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


